// Cursor paging on top of useQuery: the first page is a normal query (cached,
// refetched on invalidate, aborted on filter change); "Load more" appends the
// next pages. Changing the key (filters) starts over from the first page.

import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from '@/api/client'
import { hashKey, useQuery, type QueryKey } from '@/lib/query'

export interface CursorPage<T> {
  entries: T[]
  next_cursor: number | null
}

export function useCursorPages<T, P extends CursorPage<T>>(key: QueryKey | null, fetchPage: (cursor: number | null, signal?: AbortSignal) => Promise<P>) {
  const first = useQuery(key, (signal) => fetchPage(null, signal), { keepPrevious: true })
  const hashed = key ? hashKey(key) : ''
  const [more, setMore] = useState<{ key: string; entries: T[]; next: number | null | undefined; loading: boolean; error: string | null }>({
    key: hashed,
    entries: [],
    next: undefined,
    loading: false,
    error: null,
  })
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage

  // New filters (or a refetch of page one): drop the appended pages.
  useEffect(() => {
    setMore({ key: hashed, entries: [], next: undefined, loading: false, error: null })
  }, [hashed, first.data])

  const current = more.key === hashed ? more : { entries: [] as T[], next: undefined, loading: false, error: null }
  const nextCursor = current.next !== undefined ? current.next : (first.data?.next_cursor ?? null)

  const loadMore = useCallback(async () => {
    if (nextCursor == null) return
    setMore((m) => ({ ...m, loading: true, error: null }))
    try {
      const page = await fetchRef.current(nextCursor)
      setMore((m) => (m.key !== hashed ? m : { key: hashed, entries: [...m.entries, ...page.entries], next: page.next_cursor, loading: false, error: null }))
    } catch (err) {
      setMore((m) => ({ ...m, loading: false, error: errorMessage(err) }))
    }
  }, [nextCursor, hashed])

  return {
    first,
    entries: [...(first.data?.entries ?? []), ...current.entries],
    nextCursor,
    loadMore,
    loadingMore: current.loading,
    moreError: current.error,
  }
}
